import {
  Feature,
  feature,
  FeatureCollection,
  featureCollection,
  Point,
  Position,
} from '@turf/helpers'
import { useEffect, useMemo, useReducer } from 'react'
import OverlaySidebar from '../../ui/shared/overlay-sidebar'
import FullScreenOverlay from '../../ui/shared/overlays/fullscreen-overlay'
import { ProgressCard } from '../../ui/shared/progress-card'
import { getDistance } from 'ol/sphere'
import produce from 'immer'
import {
  Images,
  Gcps,
  ProjectFile,
  ProjectFileId,
  GcpId,
  Gcp,
  Image,
  RegisterGcpsCallback,
  Markers,
} from './types'
import MapView from './map-view'
import Button from '../../ui/elements/button'
import ValidationMessage from '../../ui/shared/validation-message'
import { getImage } from '../../../exif-utils'
import getThumbnail from './thumbnail'
import { compare } from '../../../object-utils'
import { formatCount } from '../../../string-utils'

type RegisterGcpsProps = {
  sourceCrs: string
  gcps: Gcps
  markers?: Markers
  images: FeatureCollection<Point>
  files: Record<ProjectFileId, ProjectFile>
  mapboxToken: string
  onRegister: RegisterGcpsCallback
  onCancel: () => void
}

type RegisterGcpState = {
  gcps: Gcps
  images: Images
  selectedGcpId?: GcpId
  selectedImageId?: ProjectFileId
  markers: Markers
}

type SelectImage = { type: 'SELECT_IMAGE'; imageId: ProjectFileId }
type SelectGcp = { type: 'SELECT_GCP'; gcpId: GcpId }
type DeleteGcp = { type: 'DELETE_GCP'; gcpId: GcpId }
type UpdateMarker = {
  type: 'UPDATE_MARKER'
  imageId: string
  include: boolean
  coordinates: Position
  thumbnailUrl?: string
}
type Thumbnail = { gcpId: GcpId; imageId: ProjectFileId; thumbnailUrl: string }
type SetThumbnails = { type: 'SET_THUMBNAILS'; thumbnails: Thumbnail[] }

type RegisterGcpAction =
  | SelectImage
  | SelectGcp
  | DeleteGcp
  | UpdateMarker
  | SetThumbnails

export default function RegisterGcps({
  sourceCrs,
  gcps: initialGcps,
  markers: initialMarkers = {},
  images: initialImages,
  files,
  mapboxToken,
  onRegister,
  onCancel,
}: RegisterGcpsProps) {
  const [
    { gcps, selectedGcpId, selectedImageId, markers, images },
    dispatch,
  ] = useReducer(reducer, null, createInitialState)

  const visibleCaptures = useMemo(findClosestCaptures, [
    images,
    gcps,
    selectedGcpId,
  ])
  const visibleImages = useMemo(
    () =>
      featureCollection(
        visibleCaptures.map(capture => getImageByBandPriority(capture))
      ),
    [visibleCaptures]
  )

  useEffect(() => {
    if (
      visibleImages.features.length > 0 &&
      !visibleImages.features.some(
        image => image.properties.id === selectedImageId
      )
    ) {
      dispatch({
        type: 'SELECT_IMAGE',
        imageId: visibleImages.features[0].properties.id,
      })
    }
  }, [visibleImages, selectedImageId])

  const selectedImage = visibleImages.features.find(
    image => image.properties.id === selectedImageId
  )
  const selectedImageFile = useMemo(
    () =>
      selectedImage
        ? files[
            selectedImage.properties.originalImageFeature.id?.toString() || ''
          ]
        : undefined,
    [selectedImage, files]
  )

  const marker =
    selectedGcpId && selectedImageId
      ? markers[selectedGcpId]?.[selectedImageId]
      : undefined

  const [isValid, message] = useMemo(() => {
    const completedGcps = gcps.features.filter(
      gcp => gcp.properties.numberMarkers >= 3
    )
    const incompleteGcps = gcps.features.filter(
      gcp =>
        gcp.properties.numberMarkers > 0 && gcp.properties.numberMarkers < 3
    )
    return [
      completedGcps.length >= 4,
      completedGcps.length < 4
        ? 'Please register at least four GCPs with at least three images in each.'
        : incompleteGcps.length > 0
        ? `${completedGcps.length} of ${formatCount(
            incompleteGcps.length + completedGcps.length,
            'GCP'
          )} will be used, please register at least three images in each GCP.`
        : null,
    ]
  }, [gcps])

  return (
    <FullScreenOverlay onDismiss={onCancel} show={true} dismissOnEscape={false}>
      <div className="tw-flex tw-h-full">
        <OverlaySidebar
          onDismiss={onCancel}
          canDismiss={true}
          backButtonTitle="Back"
          title="Register GCPs">
          <div className="tw-h-full tw-py-5 tw-flex tw-flex-col tw-justify-between tw-space-y-4">
            <div className="tw-px-4">
              <ValidationMessage>{message}</ValidationMessage>
            </div>
            <div className="tw-flex-grow tw-overflow-auto tw-px-4 tw-py-2 tw-border-b tw-border-t tw-border-system-grey-200">
              {gcps.features.map(feature => (
                <div className="tw-mb-3" key={feature.properties?.name}>
                  <ProgressCard
                    numberCompleted={feature.properties.numberMarkers}
                    numberTotal={3}
                    active={feature.properties?.id === selectedGcpId}
                    onClick={() =>
                      dispatch({
                        type: 'SELECT_GCP',
                        gcpId: feature.properties.id,
                      })
                    }>
                    <div className="tw-ml-4 tw-w-full tw-flex tw-flex-row tw-justify-between tw-items-center">
                      <div>
                        <div>{feature.properties?.name}</div>
                        <div className="tw-text-xs tw-text-system-grey-300">
                          {feature.geometry.coordinates
                            .map((c, i) => c.toFixed(i < 2 ? 5 : 1))
                            .join(', ')}
                        </div>
                        <div className="tw-flex tw-flex-wrap">
                          {markers[feature.properties.id] &&
                            Object.keys(markers[feature.properties.id]).map(
                              imageId => {
                                const marker =
                                  markers[feature.properties.id][imageId]
                                return (
                                  marker.properties.include &&
                                  marker.properties.thumbnailUrl && (
                                    <img
                                      key={`${imageId}`}
                                      className="tw-w-12 tw-h-12 tw-mr-2 tw-mt-2"
                                      src={marker.properties.thumbnailUrl}
                                    />
                                  )
                                )
                              }
                            )}
                        </div>
                      </div>
                    </div>
                  </ProgressCard>
                </div>
              ))}
            </div>
            <div className="tw-px-4 tw-flex tw-flex-col tw-space-y-4">
              <Button
                size="large"
                action="success"
                disabled={!isValid}
                onClick={() =>
                  onRegister({
                    crs: sourceCrs,
                    gcps: gcps.features.map(gcp => ({
                      id: gcp.properties.id,
                      coordinates: gcp.properties.sourceCoordinates,
                      wgs84Coordinates: gcp.geometry.coordinates,
                    })),
                    markers: Object.keys(markers)
                      .map(gcpId =>
                        Object.keys(markers[gcpId])
                          .filter(
                            imageId =>
                              markers[gcpId][imageId].properties.include &&
                              markers[gcpId][imageId].geometry
                          )
                          .map(imageId => {
                            // Marker's state use OpenLayers coordinates where Y is flipped,
                            // result should have "normal" Y coordinates where 0 is top and
                            // grows downwards
                            const olCoord = (markers[gcpId][imageId] as Feature<
                              Point
                            >).geometry.coordinates
                            const coordinates = [olCoord[0], -olCoord[1]]
                            return {
                              gcpId,
                              imageFilename: imageId,
                              coordinates,
                            }
                          })
                      )
                      .flat(),
                  })
                }>
                Register GCPs
              </Button>
              <Button size="large" action="default" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </OverlaySidebar>
        <MapView
          images={visibleImages}
          gcps={gcps}
          mapboxToken={mapboxToken}
          selectedImage={selectedImage}
          selectedImageFile={selectedImageFile?.file}
          selectedGcp={gcps.features.find(
            gcp => gcp.properties.id === selectedGcpId
          )}
          includeMarker={marker?.properties.include || false}
          markerCoordinates={marker?.geometry?.coordinates}
          onSelect={feature =>
            feature.properties && feature.properties.type === 'gcp'
              ? dispatch({
                  type: 'SELECT_GCP',
                  gcpId: (feature as Gcp).properties.id,
                })
              : dispatch({
                  type: 'SELECT_IMAGE',
                  imageId: (feature as Image).properties.id,
                })
          }
          updateMarker={(include, coordinates, thumbnailUrl) =>
            selectedImageId &&
            dispatch({
              type: 'UPDATE_MARKER',
              imageId: selectedImageId,
              include,
              coordinates,
              thumbnailUrl,
            })
          }
        />
      </div>
    </FullScreenOverlay>
  )

  function createInitialState(): RegisterGcpState {
    const selectedGcp = initialGcps.features[0]
    const images = featureCollection(
      initialImages.features.map(f => ({
        ...f,
        properties: {
          id: f.id?.toString() || '',
          originalImageFeature: f,
          include: Object.keys(initialMarkers).reduce((a, gcpId) => {
            a[gcpId] =
              initialMarkers[gcpId]?.[
                f.id?.toString() || ''
              ]?.properties.include
            return a
          }, {} as Record<GcpId, boolean>),
        },
      }))
    )

    const state = {
      gcps: featureCollection([...initialGcps.features]),
      images,
      markers: initialMarkers,
      selectedGcpId: selectedGcp.properties.id,
    }

    fetchThumbnails(state.images, state.markers)

    return state
  }

  function findClosestCaptures() {
    const selectedGcp = gcps.features.find(
      gcp => gcp.properties.id === selectedGcpId
    )

    if (selectedGcp) {
      return getClosestCaptures(selectedGcp, images).slice(0, 10)
    } else {
      return []
    }
  }

  async function fetchThumbnails(images: Images, markers: Markers) {
    const thumbnails: Thumbnail[] = []
    for (const gcpId in markers) {
      const gcpMarkers = markers[gcpId]
      for (const imageId in gcpMarkers) {
        const marker = gcpMarkers[imageId]
        if (marker.geometry) {
          const imageFeature = images.features.find(
            image => image.properties.id === imageId
          )
          if (!imageFeature) {
            throw new Error(`Could not find image ${imageId}.`)
          }
          const image = await getImage(
            imageFeature.properties.originalImageFeature,
            () => files[imageId].file,
            {
              revokeObjectURL: false,
            }
          )
          await imageLoaded(image)

          thumbnails.push({
            gcpId,
            imageId,
            thumbnailUrl: getThumbnail(image, marker.geometry.coordinates),
          })
          URL.revokeObjectURL(image.src)
        }
      }
    }

    if (thumbnails.length > 0) {
      dispatch({ type: 'SET_THUMBNAILS', thumbnails })
    }
  }
}

function imageLoaded(image: HTMLImageElement): Promise<void> {
  return new Promise((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = e =>
      reject(new Error(`Failed to load image: ${e.toString()}`))
  })
}

function reducer(
  state: RegisterGcpState,
  action: RegisterGcpAction
): RegisterGcpState {
  switch (action.type) {
    case 'SELECT_GCP': {
      const selectedGcp = state.gcps.features.find(
        gcp => gcp.properties.id === action.gcpId
      )

      if (!selectedGcp) {
        throw new Error(`No GCP with id ${action.gcpId}`)
      }

      return {
        ...state,
        selectedGcpId: action.gcpId,
      }
    }
    case 'DELETE_GCP': {
      const gcpIndex = state.gcps.features.findIndex(
        gcp => gcp.properties.id === action.gcpId
      )
      if (gcpIndex < 0) {
        throw new Error(`No GCP with id ${action.gcpId}.`)
      }
      return produce(state, draft => {
        draft.gcps.features.splice(gcpIndex, 1)
        delete draft.markers[action.gcpId]
        if (draft.selectedGcpId === action.gcpId) {
          draft.selectedGcpId = undefined
        }
      })
    }
    case 'SELECT_IMAGE': {
      if (!state.selectedGcpId) {
        throw new Error(`Selecting an image without a selected GCP`)
      }

      const { selectedGcpId } = state
      const selectedImageId = action.imageId

      return produce(state, draft => {
        draft.selectedImageId = action.imageId
        ensureGcpImageExists(draft, selectedGcpId, selectedImageId)
      })
    }
    case 'UPDATE_MARKER': {
      if (!state.selectedGcpId) {
        throw new Error(`Selecting an image without a selected GCP`)
      }

      const { selectedGcpId, selectedImageId } = state

      return produce(state, draft => {
        if (!selectedImageId) {
          throw new Error('No image selected.')
        }
        ensureGcpImageExists(draft, selectedGcpId, selectedImageId)
        const markerFeature = draft.markers[selectedGcpId][action.imageId]
        if (!markerFeature.geometry) {
          markerFeature.geometry = {
            type: 'Point',
            coordinates: action.coordinates,
          }
        } else {
          markerFeature.geometry.coordinates = action.coordinates
        }

        markerFeature.properties.include = action.include
        if (action.thumbnailUrl) {
          markerFeature.properties.thumbnailUrl = action.thumbnailUrl
        }

        const selectedGcp = draft.gcps.features.find(
          gcp => gcp.properties.id === selectedGcpId
        )
        if (selectedGcp) {
          selectedGcp.properties.numberMarkers = Object.values(
            draft.markers[selectedGcpId]
          ).filter(marker => marker.properties.include).length
        }
        const selectedImage = draft.images.features.find(
          image => image.properties.id === selectedImageId
        )
        if (selectedImage) {
          selectedImage.properties.include[selectedGcpId] = action.include
        }
      })
    }
    case 'SET_THUMBNAILS':
      return produce(state, draft => {
        for (const thumbnail of action.thumbnails) {
          if (draft.markers[thumbnail.gcpId]?.[thumbnail.imageId])
            draft.markers[thumbnail.gcpId][
              thumbnail.imageId
            ].properties.thumbnailUrl = thumbnail.thumbnailUrl
        }
      })
  }
}

function ensureGcpImageExists(
  state: RegisterGcpState,
  gcpId: GcpId,
  imageId: ProjectFileId
) {
  state.markers[gcpId] ||= {}
  state.markers[gcpId][imageId] ||= feature(null, {
    include: false,
  })
}

function getClosestCaptures(point: Feature<Point>, images: Images) {
  const captures = groupByCapture(images)

  return captures
    .filter(capture => capture.geometry)
    .sort((a, b) => {
      const distanceA = getDistance(
        point.geometry.coordinates,
        a.geometry.coordinates
      )
      const distanceB = getDistance(
        point.geometry.coordinates,
        b.geometry.coordinates
      )
      return distanceA - distanceB
    })
}

type CaptureGroup = { captureId: string; geometry: Point; images: Image[] }

function groupByCapture(images: Images): CaptureGroup[] {
  const captures: Record<string, CaptureGroup> = {}
  for (const image of images.features) {
    const captureId =
      image.properties.originalImageFeature.properties?.final_capture_id
    if (!captures[captureId]) {
      captures[captureId] = {
        captureId: captureId,
        geometry: image.geometry,
        images: [image],
      }
    } else {
      captures[captureId].images.push(image)
    }
  }

  return Object.values(captures)
}

function getImageByBandPriority(capture: CaptureGroup): Image {
  const bandPriority = ['NIR', 'Red edge', 'Red', 'Green', 'Blue']
  if (capture.images[0].properties.originalImageFeature.properties?.band) {
    for (const band of bandPriority) {
      for (const image of capture.images) {
        if (image.properties.originalImageFeature.properties?.band === band) {
          return image
        }
      }
    }
  }

  // We don't really know which image is which band, so make
  // an educated guess: if we order files alphabetically, the
  // next last band is often either green or red edge/NIR, so
  // take that one.
  capture.images.sort((a: Image, b: Image) =>
    compare(a.properties.id, b.properties.id)
  )
  return capture.images[Math.max(0, capture.images.length - 2)]
}
